import { runInAction } from "mobx";
import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
import type {
  AppStoreAudioCaptureStore,
  AutoStopReason,
} from "@/store/audio-capture/AppStoreAudioCaptureStore";

export interface AudioCaptureLockOptions {
  showErrorToast?: boolean;
}

const FAILED_TO_GET_LOCK_MESSAGE = "Failed to get lock to start recording";

export async function withAudioCaptureLock(
  store: AppStoreAudioCaptureStore,
  action: () => Promise<void>,
  options: AudioCaptureLockOptions = { showErrorToast: true }
): Promise<void> {
  if (store.status === RecordingStatus.LOCKED) {
    await action();
    return;
  }

  if (store.releaseRecordingLock) {
    logger.error({
      message: "[AppStoreAudioCaptureStore] Already have an active recording session",
    });
    return;
  }

  try {
    await navigator.locks.request(
      "audio-capture-recording",
      {
        mode: "exclusive",
        ifAvailable: true,
      },
      async lock => {
        if (!lock) throw new Error(FAILED_TO_GET_LOCK_MESSAGE);

        logger.debug({
          message: "[AppStoreAudioCaptureStore] got lock to start recording",
        });

        const waitUntilRelease = new Promise(resolve => {
          runInAction(() => {
            store.releaseRecordingLock = resolve;
          });
        });

        try {
          await action();
        } catch (error) {
          runInAction(() => {
            store.releaseRecordingLock = null;
          });
          throw error;
        }

        return waitUntilRelease;
      }
    );
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error({
      message: "[AppStoreAudioCaptureStore] Failed",
      info: {
        error: errorMessage,
      },
    });

    if (options.showErrorToast) {
      const isFailedToGetLock = errorMessage.includes(FAILED_TO_GET_LOCK_MESSAGE);
      toastModule.triggerToast({
        content: isFailedToGetLock
          ? "Cannot start recording while Mem is recording on another tab."
          : "Failed to start recording, please try again.",
        toastId: store.capturedAudioId,
      });
    }
  }
}

export function triggerAutoStop(store: AppStoreAudioCaptureStore, reason: AutoStopReason): void {
  if (store.autoStopReason) return;
  if (![RecordingStatus.RECORDING, RecordingStatus.PAUSED].includes(store.status)) return;

  store.session.updateAutoStopTriggered({ reason });

  void store.stopRecording();
}
